import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Menu, X, LogOut } from 'lucide-react';

const AFII_LOGO = "https://customer-assets.emergentagent.com/job_afii-future/artifacts/zz1rxbi3_image.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const darkPaths = ['/', '/chat', '/auth', '/mentor-waitlist'];
  const isDark = darkPaths.includes(location.pathname);

  const links = user
    ? [
        { to: '/dashboard', label: t('nav.dashboard') },
        { to: '/chat', label: t('nav.chatGina') },
        { to: '/courses', label: t('nav.courses') },
        { to: '/jobs', label: 'Jobs' },
        { to: '/field-guide', label: 'Badges' },
        ...(user.role === 'owner' || user.role === 'admin' ? [{ to: '/admin/dashboard', label: 'Admin' }] : []),
      ]
    : [
        { to: '/courses', label: t('nav.courses') },
        { to: '/jobs', label: 'Jobs' },
        { to: '/mentor-waitlist', label: t('nav.mentors') },
      ];

  return (
    <nav data-testid="navbar" className={`sticky top-0 z-50 border-b backdrop-blur-xl ${isDark ? 'bg-[#050B14]/80 border-white/10' : 'bg-white/80 border-gray-100'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src={AFII_LOGO} alt="AFII" className="w-9 h-9 rounded-lg object-contain" />
          <span className={`font-['Outfit'] font-bold text-lg ${isDark ? 'text-white' : 'text-[#0B1D3A]'}`}>AFII</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.slice(1)}`}
              className={`font-['Manrope'] text-sm font-medium transition-opacity duration-200 ${
                location.pathname === link.to
                  ? 'text-[#D4A843]'
                  : isDark ? 'text-white/60 hover:text-white' : 'text-[#0B1D3A]/60 hover:text-[#0B1D3A]'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <span className={`font-['Manrope'] text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-testid="nav-user-name">
                {user.name}
              </span>
              <Button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate('/'); }}
                variant="ghost"
                size="sm"
                className={`rounded-full ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-[#0B1D3A]/40 hover:text-[#0B1D3A] hover:bg-[#0B1D3A]/5'}`}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              data-testid="nav-signin-btn"
              onClick={() => navigate('/auth')}
              className="bg-[#D4A843] text-[#0B1D3A] font-semibold hover:bg-[#F5D78E] rounded-full px-6 text-sm"
            >
              {t('nav.signIn')}
            </Button>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden p-1"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation menu"
        >
          {mobileOpen
            ? <X className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#0B1D3A]'}`} />
            : <Menu className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#0B1D3A]'}`} />
          }
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className={`md:hidden px-6 py-4 border-t ${isDark ? 'bg-[#050B14] border-white/10' : 'bg-white border-gray-100'}`}>
          <div className="space-y-2">
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block font-['Manrope'] text-sm py-2.5 transition-opacity hover:opacity-100 ${isDark ? 'text-white/70' : 'text-[#0B1D3A]/60'}`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <button
                onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
                className={`block font-['Manrope'] text-sm py-2.5 transition-opacity hover:opacity-100 ${isDark ? 'text-white/70' : 'text-[#0B1D3A]/60'}`}
              >
                Sign Out
              </button>
            ) : (
              <Button
                onClick={() => { navigate('/auth'); setMobileOpen(false); }}
                className="w-full bg-[#D4A843] text-[#0B1D3A] font-semibold rounded-full mt-3"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
